/**
 * Filter System:
 * - System filters: Hidden from UI, marked with system: true
 * - Custom filters: Shown as chips only, identified by CUSTOM_FILTER_FIELDS
 * - User filters: Shown in chips and popover, created by users
 */
import { Filter } from '../types';

const CUSTOM_FILTER_FIELDS = new Set([
  'approvalStatus',
  'syncStatus',
  'errorMessage',
  'Status',
  'PartnerCoId'
]);

export function isSystemFilter(filter: Filter | null | undefined): boolean {
  if (!filter) return false;
  return filter.system === true;
}

export function isCustomFilter(filter: Filter | null | undefined): boolean {
  if (!filter) return false;
  return CUSTOM_FILTER_FIELDS.has(filter.field as string);
}


export function createFilter(field: string, operator: string, value?: unknown, isStatic = false): Filter {
  const filter: Filter = {
    field,
    operator
  };

  if (value !== undefined) {
    filter.value = value;
  }

  if (isStatic) {
    filter.static = true;
  }

  return filter;
}



export function extractNonSystemFilters(allFilters: Filter[]): Filter[] {
  if (!allFilters || !Array.isArray(allFilters)) {
    return [];
  }

  return allFilters.filter(f => !isSystemFilter(f));
}

export function removeSystemFiltersFromConfig(configuration: any): any {
  if (!configuration) return configuration;

  // If configuration has filters, clean them
  if (configuration.filters && configuration.filters.filters) {
    return {
      ...configuration,
      filters: {
        ...configuration.filters,
        filters: extractNonSystemFilters(configuration.filters.filters)
      }
    };
  }

  return configuration;
}


export function validateFilter(filter: any): boolean {
  if (!filter || typeof filter !== 'object') {
    console.warn('Invalid filter: filter is not an object', filter);
    return false;
  }

  if (filter.filters !== undefined) {
    if (!filter.logic || !Array.isArray(filter.filters)) {
      console.warn('Invalid filter: missing logic or filters array', filter);
      return false;
    }

    if (!['and', 'or'].includes(filter.logic)) {
      console.warn(`Invalid filter logic: '${filter.logic}' (must be 'and' or 'or')`);
      return false;
    }

    return filter.filters.every((f: any) => validateFilter(f));
  }

  if (!filter.field || !filter.operator) {
    console.warn('Invalid filter: missing field or operator', filter);
    return false;
  }

  if (filter.value === 'undefined' || filter.value === "'undefined'") {
    console.warn('Invalid filter value: undefined string literal', filter);
    return false;
  }

  return true;
}

/**
 * Sanitizes a filter value to ensure it's safe for storage and display.
 * Handles special characters and prevents issues with malformed values.
 * @param {any} value - The value to sanitize
 * @returns {any} Sanitized value
 */
function sanitizeFilterValue(value: any): any {
  if (value === null || value === undefined) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(sanitizeFilterValue);
  }

  if (typeof value === 'object') {
    return value;
  }

  if (typeof value === 'string') {
    // eslint-disable-next-line no-control-regex
    const cleaned = value.replace(/[\x00-\x1F\x7F]/g, '');
    return cleaned;
  }

  return value;
}

/**
 * Sanitizes a filter structure by removing invalid filters and cleaning up values.
 * Preserves system and custom filter flags during sanitization.
 * @param {Object} filter - The filter object to sanitize
 * @returns {Object} Sanitized filter object with valid structure
 */
export function sanitizeFilter(filter: any): { logic: string; filters: Filter[]; system?: boolean } {
  if (!filter || typeof filter !== 'object') {
    console.warn('Sanitizing invalid filter to empty filter object', filter);
    return { logic: 'and', filters: [] };
  }

  const sanitized: { logic: string; filters: Filter[]; system?: boolean } = {
    logic: ['and', 'or'].includes(filter.logic) ? filter.logic : 'and',
    filters: []
  };

  if (!Array.isArray(filter.filters)) {
    return sanitized;
  }

  sanitized.filters = filter.filters
    .filter((f: any) => f && typeof f === 'object')
    .map((subFilter: any) => {
      if (subFilter.filters !== undefined) {
        const sanitizedGroup = sanitizeFilter(subFilter);
        // Preserve system flag for nested filter groups
        if (subFilter.system === true) {
          sanitizedGroup.system = true;
        }
        return sanitizedGroup;
      }

      if (!subFilter.field || !subFilter.operator) {
        console.warn('Removing invalid filter: missing field or operator', subFilter);
        return null;
      }

      const cleanFilter: Filter = {
        field: subFilter.field,
        operator: subFilter.operator
      };

      if (subFilter.value !== undefined) {
        if (subFilter.value === 'undefined' ||
            subFilter.value === "'undefined'") {
          console.warn('Removing filter with invalid value', subFilter);
          return null;
        }
        cleanFilter.value = sanitizeFilterValue(subFilter.value);
      }
      if (subFilter.system === true) {
        cleanFilter.system = true;
      }
      if (subFilter.static === true) {
        cleanFilter.static = true;
      }

      return cleanFilter;
    })
    .filter(Boolean);

  return sanitized;
}

export function removeFiltersByField(filter: any, fields: string | string[]): { logic: string; filters: any[] } {
  if (!filter || !filter.filters) {
    return { logic: 'and', filters: [] };
  }

  const fieldsToRemove = Array.isArray(fields) ? fields : [fields];
  const fieldSet = new Set(fieldsToRemove);

  const cleaned = {
    logic: filter.logic || 'and',
    filters: filter.filters
      .map((f: any) => {
        if (f.filters !== undefined) {
          const cleanedGroup = removeFiltersByField(f, fields);
          return cleanedGroup.filters.length > 0 ? cleanedGroup : null;
        }

        return fieldSet.has(f.field) ? null : f;
      })
      .filter(Boolean)
  };

  return cleaned;
}

/**
 * Merges system filters with user filters.
 * System filters are always preserved and placed first in the filter array.
 * User filters should already have system filters removed to avoid duplication.
 * @param {Object} systemFilters - Filter object containing only system filters
 * @param {Object} userFilters - Filter object containing user-defined filters
 * @returns {Object} Merged filter object with both system and user filters
 */
export function mergeSystemAndUserFilters(
  systemFilters: { logic?: string; filters?: Filter[] } | null | undefined,
  userFilters: { logic?: string; filters?: Filter[] } | null | undefined
): { logic: string; filters: Filter[] } {
  // If no system filters, return user filters as-is
  if (!systemFilters || !systemFilters.filters || systemFilters.filters.length === 0) {
    return userFilters as { logic: string; filters: Filter[] } || { logic: 'and', filters: [] };
  }

  // If no user filters, return system filters
  if (!userFilters || !userFilters.filters || userFilters.filters.length === 0) {
    return systemFilters as { logic: string; filters: Filter[] };
  }

  const userFiltersToMerge: Filter[] = userFilters.logic === 'or'
    ? [{ logic: 'or', filters: userFilters.filters }]
    : userFilters.filters;

  // Merge both filter sets. System filters must stay mandatory, so the
  // top-level join is always AND when system and user filters are combined.
  const merged = {
    logic: 'and',
    filters: [
      // System filters come first
      ...systemFilters.filters,
      // Then user filters (already without system filters from saved config)
      ...userFiltersToMerge
    ]
  };

  return merged;
}
